import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTheme } from '../../../context/ThemeContext';
import { listComps, searchComps, createComp, resendInvite } from './api';
import CompClientsList from './CompClientsList';
import CompSearchResults from './CompSearchResults';
import AddCompClientModal from './AddCompClientModal';
import FileReviewCompsTab from '../../dashboard/views/admin/components/billing/FileReviewCompsTab';

export default function CompClientsPanel() {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [view, setView] = useState('clients'); // 'clients' | 'companies'

  const { data: list } = useQuery({ queryKey: ['comps', 'mine'], queryFn: () => listComps('mine') });
  const { data: search } = useQuery({
    queryKey: ['comp-search', q], queryFn: () => searchComps(q), enabled: q.trim().length >= 2,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['comps'] });
    qc.invalidateQueries({ queryKey: ['comp-search'] });
  };

  const comp = async (r) => {
    try {
      const res = await createComp({ source_type: r.kind === 'candidate' ? 'candidate' : 'client', source_id: r.id });
      toast.success(res.result === 'already_comped' ? 'Already comped' : 'Client comped');
      refresh();
    } catch (e) { toast.error(e.message); }
  };
  const resend = async (r) => {
    try {
      await resendInvite(r.kind === 'candidate' ? 'candidate' : 'client', r.id);
      toast.success('Invitation resent');
    } catch (e) { toast.error(e.message); }
  };

  const tabBtn = (active) => `px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
    active
      ? 'bg-blue-600 text-white'
      : dark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'
  }`;

  return (
    <div className="space-y-5">
      <div className={`inline-flex gap-1 rounded-lg border p-1 ${dark ? 'border-gray-700 bg-gray-800/60' : 'border-gray-200 bg-white'}`}>
        <button onClick={() => setView('clients')} className={tabBtn(view === 'clients')}>My comp clients</button>
        <button onClick={() => setView('companies')} className={tabBtn(view === 'companies')}>File Review companies</button>
      </div>

      {view === 'companies' ? (
        <FileReviewCompsTab />
      ) : (
        <>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 flex-1 max-w-md ${dark ? 'border-gray-700 bg-gray-800/60' : 'border-gray-200 bg-white'}`}>
              <Search size={16} className="text-gray-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search company or email…"
                className="bg-transparent outline-none text-sm flex-1" />
            </div>
            <button onClick={() => setShowAdd(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700">
              <Plus size={16} /> Add comp client
            </button>
          </div>

          {q.trim().length >= 2 && search
            ? <CompSearchResults results={search} onComp={comp} onResend={resend} theme={theme} />
            : (
              <div>
                <h3 className={`text-sm font-semibold mb-2 ${dark ? 'text-gray-300' : 'text-gray-600'}`}>My comped clients</h3>
                <CompClientsList comps={list?.comps} onResend={resend} theme={theme} />
              </div>
            )}
        </>
      )}

      {showAdd && (
        <AddCompClientModal
          onClose={() => setShowAdd(false)}
          onDone={() => { setShowAdd(false); refresh(); }}
          theme={theme}
        />
      )}
    </div>
  );
}
